import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import { Button, Empty, Icon, Spinner } from "../../components/ui";
import Beranda from "./Beranda";
import DaftarMurid from "./DaftarMurid";
import IsiMurid from "./IsiMurid";
import TerbitMurid from "./TerbitMurid";

// ─── Raport — halaman trainer (isi nilai & komentar, terbit & cetak) ─────────
//
// Rancangan: docs/rancangan-adopsi-raport.md §7.3. Empat layar, semuanya di menu yang sama:
//   beranda (pilih term, kartu kelas) → daftar murid satu kelas → isi raport satu murid
//   → pratinjau, terbit & cetak raport murid itu.
// Layarnya disimpan di URL (/trainer-raport/kelas/201/murid/BR01-3A26/raport/15?term=12), jadi
// tombol Back di HP mundur satu layar — bukan keluar dari Raport — dan refresh tetap di
// tempatnya. Shell hanya membaca segmen pertama URL, jadi sub-jalur ini tak mengganggu
// routing-nya.
//
// Semua aturan (siapa boleh mengisi, kunci term, kelas utama, syarat terbit) diperiksa SERVER;
// halaman ini hanya menampilkan jawabannya dan menghitung nilai bulan seketika untuk
// tanggapan cepat.

const AKAR = "/trainer-raport";

function bacaUrl() {
  const bagian = window.location.pathname.replace(/^\/+/, "").split("/").map(decodeURIComponent);
  const term = Number(new URLSearchParams(window.location.search).get("term")) || null;
  const classId = bagian[1] === "kelas" && Number(bagian[2]) > 0 ? Number(bagian[2]) : null;
  const studentId = classId && bagian[3] === "murid" && bagian[4] ? bagian[4] : null;
  const raport = !!studentId && bagian[5] === "raport";
  return {
    classId,
    studentId,
    layar: raport ? "raport" : null,
    versi: raport && Number(bagian[6]) > 0 ? Number(bagian[6]) : null,
    term
  };
}

function urlDari({ classId, studentId, layar, versi, term }) {
  let p = AKAR;
  if (classId) p += `/kelas/${classId}`;
  if (classId && studentId) p += `/murid/${encodeURIComponent(studentId)}`;
  if (classId && studentId && layar === "raport") p += versi ? `/raport/${versi}` : "/raport";
  return term ? `${p}?term=${term}` : p;
}

export default function RaportPage({ setToast }) {
  const [awal] = useState(bacaUrl);
  const [bank, setBank] = useState(null);
  const [terms, setTerms] = useState(null);
  const [termId, setTermId] = useState(awal.term);
  const [lokasi, setLokasi] = useState({
    classId: awal.classId, studentId: awal.studentId, layar: awal.layar, versi: awal.versi
  });
  const [kelasSaya, setKelasSaya] = useState(null);
  const [rincian, setRincian] = useState(null);
  const [memuat, setMemuat] = useState(true);
  const [galat, setGalat] = useState("");
  const [ulang, setUlang] = useState(0);
  const nomor = useRef(0);

  // ── Bahan awal: bank komentar & daftar term ────────────────────────────────
  useEffect(() => {
    let batal = false;
    setMemuat(true);
    Promise.all([api.getRaportBank(), api.getRaportTerms()])
      .then(([b, t]) => {
        if (batal) return;
        setBank(b);
        setTerms(t);
        setTermId((id) => (id && t.term.some((x) => x.id === id) ? id : t.sekarang || (t.term[0] && t.term[0].id) || null));
        setGalat("");
      })
      .catch((err) => { if (!batal) setGalat(err.message || "Raport belum bisa dimuat."); })
      .finally(() => { if (!batal) setMemuat(false); });
    return () => { batal = true; };
  }, [ulang]);

  // ── Kartu kelas (beranda) ─────────────────────────────────────────────────
  useEffect(() => {
    if (!termId || lokasi.classId) return;
    const ini = ++nomor.current;
    api.getRaportKelasSaya(termId)
      .then((r) => { if (ini === nomor.current) setKelasSaya(r); })
      .catch((err) => { if (ini === nomor.current) setGalat(err.message || "Daftar kelas gagal dimuat."); });
  }, [termId, lokasi.classId, ulang]);

  // ── Isian satu kelas ──────────────────────────────────────────────────────
  const muatKelas = useCallback(() => {
    if (!termId || !lokasi.classId) return;
    const ini = ++nomor.current;
    api.getRaportKelas(lokasi.classId, termId)
      .then((r) => { if (ini === nomor.current) { setRincian(r); setGalat(""); } })
      .catch((err) => { if (ini === nomor.current) { setRincian(null); setGalat(err.message || "Isian kelas gagal dimuat."); } });
  }, [termId, lokasi.classId]);

  useEffect(() => {
    if (!lokasi.classId) { setRincian(null); return; }
    if (rincian && rincian.kelas.id === lokasi.classId && rincian.term.id === termId) return;
    muatKelas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lokasi.classId, termId, muatKelas]);

  // Kembali ke daftar murid dari layar satu murid: muat ulang, supaya status terbit (dan
  // "berubah sejak terbit") mengikuti apa yang baru saja diisi atau diterbitkan.
  const tadiDiMurid = useRef(!!awal.studentId);
  useEffect(() => {
    if (lokasi.classId && !lokasi.studentId && tadiDiMurid.current && rincian) muatKelas();
    tadiDiMurid.current = !!lokasi.studentId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lokasi.classId, lokasi.studentId]);

  // ── Navigasi antar-layar lewat URL ────────────────────────────────────────
  useEffect(() => {
    const onPop = () => {
      const u = bacaUrl();
      setLokasi({ classId: u.classId, studentId: u.studentId, layar: u.layar, versi: u.versi });
      if (u.term) setTermId(u.term);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // `ganti`: mengganti entri riwayat peramban (mis. memilih versi raport), bukan menambah.
  const pergi = (tujuan, { ganti = false } = {}) => {
    const baru = {
      classId: tujuan.classId || null,
      studentId: tujuan.studentId || null,
      layar: tujuan.layar || null,
      versi: tujuan.versi || null
    };
    try {
      window.history[ganti ? "replaceState" : "pushState"]({}, "", urlDari({ ...baru, term: termId }));
    } catch (e) { /* abaikan */ }
    setLokasi(baru);
    setGalat("");
  };

  const pilihTerm = (id) => {
    if (id === termId) return;
    try { window.history.replaceState({}, "", urlDari({ ...lokasi, term: id })); } catch (e) { /* abaikan */ }
    setKelasSaya(null);
    setRincian(null);
    setTermId(id);
  };

  // Isian satu murid berubah (sebelum & sesudah disimpan): ganti di daftar kelasnya.
  const perbaruiMurid = useCallback((murid) => {
    setRincian((r) => r && {
      ...r,
      murid: r.murid.map((m) => (m.studentId === murid.studentId ? { ...m, ...murid } : m))
    });
  }, []);

  if (memuat) return <Spinner label="Memuat raport…" />;

  if (galat && !bank) {
    return (
      <div className="anim-fade space-y-4">
        <Empty label={galat} />
        <div className="text-center">
          <Button variant="secondary" onClick={() => setUlang((n) => n + 1)}>
            <Icon name="refresh" />
            Coba lagi
          </Button>
        </div>
      </div>
    );
  }

  const term = terms ? terms.term.find((t) => t.id === termId) : null;

  if (lokasi.classId && lokasi.studentId && lokasi.layar === "raport" && termId) {
    const { classId, studentId } = lokasi;
    return (
      <TerbitMurid
        key={`${classId}|${studentId}|${termId}`}
        classId={classId}
        studentId={studentId}
        termId={termId}
        versi={lokasi.versi}
        setToast={setToast}
        onPilihVersi={(v) => pergi({ classId, studentId, layar: "raport", versi: v }, { ganti: true })}
        onKembali={() => pergi({ classId, studentId })}
      />
    );
  }

  if (lokasi.classId && lokasi.studentId && rincian) {
    return (
      <IsiMurid
        key={`${rincian.kelas.id}|${lokasi.studentId}`}
        bank={bank}
        rincian={rincian}
        studentId={lokasi.studentId}
        onPerbarui={perbaruiMurid}
        onMuatUlang={muatKelas}
        onPindah={(sid) => pergi({ classId: lokasi.classId, studentId: sid })}
        onKembali={() => pergi({ classId: lokasi.classId })}
        onPratinjau={() => pergi({ classId: lokasi.classId, studentId: lokasi.studentId, layar: "raport" })}
      />
    );
  }

  if (lokasi.classId) {
    return (
      <DaftarMurid
        rincian={rincian}
        galat={galat}
        onBukaMurid={(sid) => pergi({ classId: lokasi.classId, studentId: sid })}
        onKembali={() => pergi({})}
      />
    );
  }

  return (
    <Beranda
      terms={terms ? terms.term : []}
      term={term}
      termSekarang={terms ? terms.sekarang : null}
      kelasSaya={kelasSaya}
      galat={galat}
      onPilihTerm={pilihTerm}
      onBukaKelas={(id) => pergi({ classId: id })}
    />
  );
}
